import type { ColumnDef } from '@tanstack/react-table';
import Decimal from 'decimal.js-light';

import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { fontStyles } from '@/styles/typography';

import { AvatarStack } from '@/ui/Avatars/AvatarStack';
import { ChainAvatar, chainAvatarRadius } from '@/ui/Avatars/ChainAvatar';
import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { ChevronTriangleDownSmallIcon } from '@/ui/Icons/ChevronTriangleDownSmallIcon';

import { classNames } from '@/utils/classnames';

import { PortfolioActionsCell } from './Cells/PortfolioActionsCell';
import { PortfolioHeaderCell } from './Cells/PortfolioHeaderCell';
import type { PortfolioRow, PortfolioTableMeta } from './types';
import { formatTokenBalance, getAvgEntryPrice } from './utils';

function PnlArrow({
  isPositive,
  width = 5,
  height = 4,
}: {
  isPositive: boolean;
  width?: number;
  height?: number;
}) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 5 4"
      fill="currentColor"
      className={isPositive ? 'rotate-180' : undefined}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M0.895961 0C0.151769 0 -0.266853 0.812319 0.190039 1.36983L1.66724 3.17233C2.02528 3.60922 2.72104 3.60922 3.07908 3.17233L4.55628 1.36983C5.01317 0.812321 4.59455 0 3.85036 0H0.895961Z" />
    </svg>
  );
}

function getPnlColorClass(unrealizedGain: number | null): string {
  if (unrealizedGain == null) return 'text-token-gray-600';
  if (unrealizedGain > 0) return 'text-token-green-1000';
  if (unrealizedGain < 0) return 'text-token-red-900';
  return 'text-token-gray-600';
}

function getMeta(table: { options: { meta?: unknown } }): PortfolioTableMeta {
  return (table.options.meta as { portfolioMeta: PortfolioTableMeta }).portfolioMeta;
}

export const portfolioColumns: ColumnDef<PortfolioRow, unknown>[] = [
  {
    id: 'tokenInfo',
    // Search input renders in the table-wide header row, not per-column
    header: () => null,
    cell: ({ row }) => {
      const { token, group } = row.original;
      const depth = row.depth;
      const isExpanded = row.getIsExpanded();

      if (depth === 0 && group) {
        const primaryPosition = group.positions[0];
        const chainIds = group.positions.map((p) => p.chainId);
        const displayName = group.symbol || group.name || 'Unknown';
        const displaySubtitle = group.name || '';

        return (
          <div className="flex items-center min-w-0 relative h-full">
            {/* Connector line from parent row down to child rows */}
            {isExpanded && (
              <div
                className="absolute w-px bg-token-gray-500"
                style={{ left: 40, top: '50%', height: '50%' }}
              />
            )}

            <ChevronTriangleDownSmallIcon
              className={classNames(
                'w-5 h-5 transition-all shrink-0',
                isExpanded ? 'text-token-gray-1600' : 'text-token-gray-600 -rotate-90',
              )}
            />

            <TokenAvatar
              chainId={primaryPosition.chainId}
              address={primaryPosition.tokenAddress}
              showChainLogo={false}
              size={32}
              className="shrink-0 ml-1 mr-3"
            />

            <div className="min-w-0 flex-1">
              <p
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'text-token-gray-1600 truncate',
                })}
              >
                {displaySubtitle || displayName}
              </p>
              <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
                <span
                  className={fontStyles({
                    variant: 'BODY_MD',
                    class: 'text-token-gray-800 truncate',
                  })}
                >
                  {displayName}
                </span>
                <AvatarStack
                  className="shrink-0"
                  size={16}
                  overlap={2}
                  borderRadius={chainAvatarRadius}
                  maxItems={4}
                >
                  {chainIds.map((chainId) => (
                    <ChainAvatar key={chainId} size={16} chainId={chainId} />
                  ))}
                </AvatarStack>
              </div>
            </div>
          </div>
        );
      }

      if (depth > 0) {
        const parentRow = row.getParentRow();
        const isLastSubRow =
          parentRow != null && parentRow.subRows[parentRow.subRows.length - 1].id === row.id;
        const chainName = NETWORK_NAME_PER_CHAIN_ID[token.chainId] || 'Unknown';

        return (
          <div className="flex items-center gap-3 min-w-0 pl-[58px] relative h-full">
            {/* Vertical line segment */}
            <div
              className="absolute w-px bg-token-gray-500"
              style={{
                left: 40,
                top: 0,
                height: isLastSubRow ? 'calc(50% - 10px)' : '100%',
              }}
            />
            {/* Curved horizontal branch */}
            <div
              className="absolute w-[12px] h-[10px] border-l border-b border-token-gray-500 rounded-bl-[8px]"
              style={{ left: 40, top: 'calc(50% - 10px)' }}
            />

            <ChainAvatar chainId={token.chainId} size={20} className="shrink-0" />
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-900',
              })}
            >
              {chainName}
            </span>
          </div>
        );
      }

      const displayName =
        token.symbol ||
        token.name ||
        `${token.tokenAddress.slice(0, 6)}...${token.tokenAddress.slice(-4)}`;

      return (
        <div className="flex items-center gap-3 min-w-0 pl-6">
          <TokenAvatar
            chainId={token.chainId}
            address={token.tokenAddress}
            size={32}
            className="shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-1600 truncate',
              })}
            >
              {token.name || displayName}
            </p>
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'block text-token-gray-800 truncate',
              })}
            >
              {displayName}
            </span>
          </div>
        </div>
      );
    },
  },

  {
    id: 'value',
    header: (props) => <PortfolioHeaderCell title="Value" property="value" sortable {...props} />,
    cell: ({ row, table }) => {
      const { token, group } = row.original;
      const depth = row.depth;
      const meta = getMeta(table);

      if (depth === 0 && group) {
        const displayName = group.symbol || group.name || 'Unknown';
        const totalBalance = group.positions.reduce((sum, p) => {
          const balance = parseFloat(p.balanceFormatted || '0');
          return sum + balance;
        }, 0);
        const formattedBalanceStr = formatTokenBalance(totalBalance);

        return (
          <div className="flex flex-col items-start">
            <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-1600' })}>
              {meta.formatFiat(group.totalBalanceUsd)}
            </span>
            <span className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-800' })}>
              {formattedBalanceStr} {displayName}
            </span>
          </div>
        );
      }

      if (depth > 0) {
        const displayName =
          row.getParentRow()?.original.group?.symbol ||
          row.getParentRow()?.original.group?.name ||
          'Unknown';
        const formattedBalance = token.balanceFormatted
          ? new Decimal(token.balanceFormatted)
          : new Decimal(0);
        const balanceNum = formattedBalance.toNumber();
        const formattedBalanceStr = formatTokenBalance(balanceNum);
        const positionValue = token.balanceUsd ?? 0;

        return (
          <div className="flex flex-col items-start">
            <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-900' })}>
              {meta.formatFiat(positionValue)}
            </span>
            <span
              className={fontStyles({
                variant: 'CAPTION',
                class: 'text-token-alphalight-700',
              })}
            >
              {formattedBalanceStr} {displayName}
            </span>
          </div>
        );
      }

      const displayName =
        token.symbol ||
        token.name ||
        `${token.tokenAddress.slice(0, 6)}...${token.tokenAddress.slice(-4)}`;
      const formattedBalance = token.balanceFormatted
        ? new Decimal(token.balanceFormatted)
        : new Decimal(0);
      const balanceNum = formattedBalance.toNumber();
      const formattedBalanceStr = formatTokenBalance(balanceNum);

      return (
        <div className="flex flex-col items-start">
          <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-1600' })}>
            {token.balanceUsd != null ? meta.formatFiat(token.balanceUsd) : '-'}
          </span>
          <span className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-800' })}>
            {formattedBalanceStr} {displayName}
          </span>
        </div>
      );
    },
  },

  {
    id: 'price',
    header: () => (
      <span
        className={fontStyles({
          variant: 'LABEL_SM',
          class: 'text-token-gray-700',
        })}
      >
        Price
      </span>
    ),
    cell: ({ row, table }) => {
      const { token, group } = row.original;
      const depth = row.depth;
      const meta = getMeta(table);

      const priceUsd = depth === 0 && group ? group.priceUsd : token.priceUsd;
      const priceChangePercent1d =
        depth === 0 && group ? group.priceChangePercent1d : token.priceChangePercent1d;

      return (
        <div className="flex flex-col items-start">
          <span
            className={fontStyles({
              variant: 'BODY_MD',
              class: depth > 0 ? 'text-token-gray-800' : 'text-token-gray-1600',
            })}
          >
            {priceUsd != null ? meta.formatFiat(priceUsd, true) : '-'}
          </span>
          {priceChangePercent1d != null && priceChangePercent1d !== 0 && (
            <span
              className={fontStyles({
                variant: 'CAPTION',
                class: classNames(
                  'flex items-center gap-1',
                  priceChangePercent1d > 0 ? 'text-token-green-1000' : 'text-token-red-900',
                ),
              })}
            >
              <PnlArrow isPositive={priceChangePercent1d > 0} />
              {Math.abs(priceChangePercent1d).toFixed(2)}%
            </span>
          )}
        </div>
      );
    },
  },

  {
    id: 'avgEntryPrice',
    header: () => (
      <span
        className={fontStyles({
          variant: 'LABEL_SM',
          class: 'text-token-gray-700',
        })}
      >
        Avg. Entry
      </span>
    ),
    cell: ({ row, table }) => {
      const { pnlData } = row.original;
      const depth = row.depth;
      const meta = getMeta(table);

      if (!pnlData) {
        return (
          <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-600' })}>
            -
          </span>
        );
      }

      const avgEntryPrice = getAvgEntryPrice(row.original);
      if (avgEntryPrice === undefined) {
        return (
          <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-600' })}>
            -
          </span>
        );
      }

      return (
        <span
          className={fontStyles({
            variant: 'BODY_MD',
            class: depth > 0 ? 'text-token-gray-800' : 'text-token-gray-1600',
          })}
        >
          {meta.formatFiat(avgEntryPrice, true)}
        </span>
      );
    },
  },

  {
    id: 'unrealizedReturn',
    header: (props) => (
      <PortfolioHeaderCell title="Return %" property="unrealizedReturn" sortable {...props} />
    ),
    cell: ({ row }) => {
      const { pnlData } = row.original;

      if (pnlData) {
        const isPositive = pnlData.unrealizedGain > 0;
        const colorClass = getPnlColorClass(pnlData.unrealizedGain);

        return (
          <span
            className={fontStyles({
              variant: 'BODY_MD',
              class: classNames(colorClass, 'flex items-center gap-1'),
            })}
          >
            <PnlArrow isPositive={isPositive} width={5.5} />
            {Math.abs(pnlData.percentage).toFixed(1)}%
          </span>
        );
      }

      return (
        <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-600' })}>-</span>
      );
    },
  },

  {
    id: 'unrealizedPnl',
    header: (props) => (
      <PortfolioHeaderCell title="Unreal. PnL" property="unrealizedPnl" sortable {...props} />
    ),
    cell: ({ row, table }) => {
      const { pnlData } = row.original;
      const meta = getMeta(table);

      if (pnlData) {
        const isPositive = pnlData.unrealizedGain > 0;
        const colorClass = getPnlColorClass(pnlData.unrealizedGain);

        return (
          <span
            className={fontStyles({
              variant: 'BODY_MD',
              class: classNames(colorClass, 'flex items-center gap-1'),
            })}
          >
            <PnlArrow isPositive={isPositive} width={5.5} />
            {meta.formatFiat(Math.abs(pnlData.unrealizedGain))}
          </span>
        );
      }

      return (
        <span className={fontStyles({ variant: 'BODY_MD', class: 'text-token-gray-600' })}>-</span>
      );
    },
  },

  {
    id: 'allocation',
    header: (props) => (
      <PortfolioHeaderCell title="Allocation" property="allocation" sortable {...props} />
    ),
    cell: ({ row, table }) => {
      const { token, group } = row.original;
      const depth = row.depth;
      const meta = getMeta(table);

      const itemValue = depth === 0 && group ? group.totalBalanceUsd : (token.balanceUsd ?? 0);
      const portfolioPercentage =
        meta.totalPortfolioValue > 0 ? (itemValue / meta.totalPortfolioValue) * 100 : null;

      if (depth > 0) {
        const positionAllocation =
          meta.totalPortfolioValue > 0
            ? ((token.balanceUsd ?? 0) / meta.totalPortfolioValue) * 100
            : null;
        return (
          <span
            className={fontStyles({
              variant: 'BODY_MD',
              class: 'text-token-gray-800',
            })}
          >
            {positionAllocation != null
              ? `${positionAllocation < 0.1 ? '<0.1' : positionAllocation.toFixed(1)}%`
              : '-'}
          </span>
        );
      }

      return (
        <span
          className={fontStyles({
            variant: 'BODY_MD',
            class: 'text-token-gray-1600',
          })}
        >
          {portfolioPercentage != null
            ? `${portfolioPercentage < 0.1 ? '<0.1' : portfolioPercentage.toFixed(1)}%`
            : '-'}
        </span>
      );
    },
  },
  {
    id: 'actions',
    header: () => (
      <span
        className={fontStyles({
          variant: 'LABEL_SM',
          class: 'block text-right text-token-gray-700',
        })}
      >
        Actions
      </span>
    ),
    cell: ({ row, table }) => (
      <div className="flex items-center justify-end text-right">
        <PortfolioActionsCell row={row} table={table} />
      </div>
    ),
  },
];
